import * as RD from '@radix-ui/react-dialog';
import { HardHat, ScrollText, Settings, X } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import type { CurrentUserDto } from '@obralink/shared';
import { Button, ProgressBar } from '@/components/ui';
import { cn } from '@/lib/cn';
import { formatBytes } from '@/lib/format';
import { MAIN, SECONDARY, type NavItem } from './sidebar';

/**
 * El menú completo en el móvil: el cajón que abre el botón de tres rayas y el
 * «Más» de la barra inferior.
 *
 * ── POR QUÉ EXISTE ─────────────────────────────────────────────────────────
 * El botón de tres rayas de la barra superior se pintaba en pantallas
 * estrechas, pero lo único que hacía era `setCollapsed(false)`: desplegar la
 * barra lateral… que en pantallas estrechas NO SE PINTA (`!isMobile &&
 * <Sidebar>`). Se pulsaba y no pasaba nada, sin error ni nada en consola, que
 * es la forma más difícil de encontrar un fallo.
 *
 * Las listas de destinos son las MISMAS que las de la barra lateral de
 * escritorio (se importan de ahí), con el mismo filtrado por permisos: un menú
 * móvil con otra lista acabaría enseñando cosas distintas según el aparato.
 */
function Destino({ item, alElegir }: { item: NavItem; alElegir: () => void }) {
  return (
    <NavLink
      to={item.to}
      onClick={alElegir}
      className={({ isActive }) =>
        cn(
          'flex min-h-11 items-center gap-3 rounded-lg px-3 text-base font-medium transition-colors',
          isActive
            ? 'bg-brand-soft text-brand-soft-fg'
            : 'text-fg-muted hover:bg-surface-2 hover:text-fg',
        )
      }
    >
      <item.icon className="size-5 shrink-0" />
      <span className="truncate">{item.label}</span>
    </NavLink>
  );
}

export function MenuMovil({
  user,
  abierto,
  onCambio,
}: {
  user: CurrentUserDto;
  abierto: boolean;
  onCambio: (abierto: boolean) => void;
}) {
  const can = (p?: string) => !p || user.permissions.includes(p);
  const cerrar = () => onCambio(false);
  const usedPct = (user.company.storageUsedBytes / user.company.storageQuotaBytes) * 100;

  const ajustes: NavItem[] = [
    ...(can('user:update') ? [{ to: '/usuarios', label: 'Usuarios', icon: Settings }] : []),
    ...(can('audit:read') ? [{ to: '/auditoria', label: 'Auditoría', icon: ScrollText }] : []),
  ];

  return (
    <RD.Root open={abierto} onOpenChange={onCambio}>
      <RD.Portal>
        <RD.Overlay className="anim-overlay fixed inset-0 z-50 bg-overlay backdrop-blur-[2px]" />
        <RD.Content
          aria-describedby={undefined}
          className="anim-drawer-left fixed inset-y-0 left-0 z-50 flex w-80 max-w-[88vw] flex-col border-r border-border bg-surface shadow-floating"
        >
          <div className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-3">
            <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-brand text-fg-on-brand">
              <HardHat className="size-4.5" />
            </div>
            <div className="min-w-0 flex-1">
              <RD.Title className="truncate text-sm font-semibold text-fg">Obralink</RD.Title>
              <p className="truncate text-xs text-fg-muted">{user.company.name}</p>
            </div>
            <RD.Close asChild>
              <Button variant="ghost" size="icon-sm" aria-label="Cerrar menú">
                <X />
              </Button>
            </RD.Close>
          </div>

          <nav className="flex-1 space-y-1 overflow-y-auto px-2 py-2" aria-label="Menú">
            {MAIN.filter((i) => can(i.permission)).map((item) => (
              <Destino key={item.to} item={item} alElegir={cerrar} />
            ))}

            <div className="my-3 h-px bg-border" />

            {SECONDARY.filter((i) => can(i.permission)).map((item) => (
              <Destino key={item.to} item={item} alElegir={cerrar} />
            ))}
            {ajustes.map((item) => (
              <Destino key={item.to} item={item} alElegir={cerrar} />
            ))}
          </nav>

          <div className="shrink-0 border-t border-border px-3 py-3">
            <div className="mb-1.5 flex items-baseline justify-between text-xs">
              <span className="font-medium text-fg-muted">Almacenamiento</span>
              <span className="tabular text-fg-subtle">{Math.round(usedPct)} %</span>
            </div>
            <ProgressBar
              value={usedPct}
              size="sm"
              tone={usedPct > 90 ? 'danger' : usedPct > 75 ? 'warning' : 'brand'}
              label="Almacenamiento usado"
            />
            <p className="mt-1.5 text-xs text-fg-subtle">
              {formatBytes(user.company.storageUsedBytes)} de{' '}
              {formatBytes(user.company.storageQuotaBytes)}
            </p>
          </div>
        </RD.Content>
      </RD.Portal>
    </RD.Root>
  );
}
